import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import Carregando from '@/components/Carregando';
import DialogoNovaClinica from '@/components/admin/DialogoNovaClinica';
import {
  calcularMrr,
  contarUso,
  ouvirClinicas,
  ouvirPlanos,
} from '@/services/adminService';

const moeda = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

const data = (valor) => {
  const d = typeof valor?.toDate === 'function' ? valor.toDate() : null;
  return d ? format(d, 'dd/MM/yy') : '—';
};

/** Painel do dono do SaaS: clínicas, planos, MRR e uso (PLANO 4.4). */
export default function AdminPage() {
  const [clinicas, setClinicas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [uso, setUso] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [novaAberta, setNovaAberta] = useState(false);

  useEffect(() => {
    const cancelar = ouvirClinicas(
      (lista) => {
        setClinicas(lista);
        setErro(null);
        setCarregando(false);
      },
      (e) => {
        console.error('[OdontoSDR] Falha ao listar clínicas:', e);
        setErro('Não foi possível carregar as clínicas.');
        setCarregando(false);
      },
    );
    return cancelar;
  }, []);

  useEffect(
    () => ouvirPlanos(setPlanos, (e) => console.error('[OdontoSDR] Falha ao listar planos:', e)),
    [],
  );

  // Contagens são agregações sob demanda, não listeners: uso muda devagar e
  // não vale manter uma assinatura por clínica aberta.
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const entradas = await Promise.all(
        clinicas.map(async (c) => [c.id, await contarUso(c.id)]),
      );
      if (!cancelado) setUso(Object.fromEntries(entradas));
    })();
    return () => {
      cancelado = true;
    };
  }, [clinicas]);

  const { mrr, semPlano } = useMemo(() => calcularMrr(clinicas, planos), [clinicas, planos]);
  const ativas = clinicas.filter((c) => c.ativa !== false).length;
  const nomePlano = (id) => planos.find((p) => p.id === id)?.nome ?? id ?? '—';

  if (carregando) return <Carregando texto="Carregando o painel…" />;

  return (
    <div className="pagina">
      <header className="pagina-cabecalho com-acao">
        <div>
          <h1>Painel SaaS</h1>
          <p className="painel-sub">Clínicas, planos e uso da plataforma.</p>
        </div>
        <button
          type="button"
          className="btn-primario compacto"
          onClick={() => setNovaAberta(true)}
          disabled={planos.length === 0}
          title={planos.length === 0 ? 'Cadastre um plano antes de criar clínicas' : undefined}
        >
          Nova clínica
        </button>
      </header>

      {erro && <p className="erro">{erro}</p>}

      <div className="indicadores">
        <div className="indicador">
          <span className="indicador-rotulo">MRR</span>
          <span className="indicador-valor">{moeda(mrr)}</span>
          {semPlano > 0 && (
            <span className="indicador-nota">
              {semPlano} clínica{semPlano === 1 ? '' : 's'} sem plano conhecido, fora da conta
            </span>
          )}
        </div>
        <div className="indicador">
          <span className="indicador-rotulo">Clínicas ativas</span>
          <span className="indicador-valor">{ativas}</span>
          {clinicas.length !== ativas && (
            <span className="indicador-nota">{clinicas.length - ativas} inativa(s)</span>
          )}
        </div>
        <div className="indicador">
          <span className="indicador-rotulo">Planos</span>
          <span className="indicador-valor">{planos.length}</span>
        </div>
      </div>

      <section className="secao">
        <h2 className="secao-titulo">Clínicas</h2>
        <div className="tabela-envolve">
          <table className="tabela">
            <thead>
              <tr>
                <th>Clínica</th>
                <th>Cidade</th>
                <th>Plano</th>
                <th>Mensalidade</th>
                <th>Usuários</th>
                <th>Leads</th>
                <th>Consultas</th>
                <th>Criada</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {clinicas.map((c) => {
                const plano = planos.find((p) => p.id === c.planoId);
                const contagem = uso[c.id];
                const limiteUsuarios = plano?.limites?.usuarios;
                const acimaDoLimite =
                  contagem?.usuarios != null &&
                  limiteUsuarios != null &&
                  contagem.usuarios > limiteUsuarios;
                return (
                  <tr key={c.id}>
                    <td>{c.nome}</td>
                    <td>{c.cidade || '—'}</td>
                    <td>{nomePlano(c.planoId)}</td>
                    <td>{plano ? moeda(plano.precoMensal) : '—'}</td>
                    <td className={acimaDoLimite ? 'acima-limite' : undefined}>
                      {contagem?.usuarios ?? '…'}
                      {limiteUsuarios != null && ` / ${limiteUsuarios}`}
                    </td>
                    <td>{contagem?.leads ?? '…'}</td>
                    <td>{contagem?.agendamentos ?? '…'}</td>
                    <td>{data(c.criadaEm)}</td>
                    <td>
                      <span
                        className="pilula pequena"
                        style={{ background: c.ativa === false ? '#64748b' : '#16a34a' }}
                      >
                        {c.ativa === false ? 'Inativa' : 'Ativa'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {clinicas.length === 0 && (
            <p className="vazio">Nenhuma clínica ainda. Crie a primeira pelo botão acima.</p>
          )}
        </div>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">Planos</h2>
        <div className="tabela-envolve">
          <table className="tabela">
            <thead>
              <tr>
                <th>Plano</th>
                <th>Mensalidade</th>
                <th>Usuários</th>
                <th>Números WhatsApp</th>
                <th>Mensagens IA</th>
                <th>Minutos de voz</th>
                <th>Clínicas</th>
              </tr>
            </thead>
            <tbody>
              {planos.map((p) => (
                <tr key={p.id}>
                  <td>{p.nome}</td>
                  <td>{moeda(p.precoMensal)}</td>
                  <td>{p.limites?.usuarios ?? '—'}</td>
                  <td>{p.limites?.numerosWhatsapp ?? '—'}</td>
                  <td>{p.limites?.mensagensIA ?? '—'}</td>
                  <td>{p.limites?.minutosVoz ?? '—'}</td>
                  <td>{clinicas.filter((c) => c.planoId === p.id).length}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {planos.length === 0 && (
            <p className="vazio">
              Nenhum plano cadastrado. Rode <code>npm run seed</code> ou crie os planos direto no
              Firestore.
            </p>
          )}
        </div>
      </section>

      <DialogoNovaClinica
        aberto={novaAberta}
        aoFechar={() => setNovaAberta(false)}
        planos={planos}
      />
    </div>
  );
}
